<script setup>
import navigation from '@/configs/footer/footer-navigation';
import socials from '@/configs/socials';
import icons from '@/configs/footer/footer-icons';
import awards from '@/configs/footer/footer-awards';

defineProps({
  showContacts: {
    type: Boolean,
    default: true,
  },
  hideMobileFooterContent: {
    type: Boolean,
    default: false,
  },
});
const { t } = useI18n();
const year = new Date().getFullYear();

const { openContactModal } = useContactModal();

const { tp } = useTypograf();
</script>

<template>
  <footer
    class="layout-footer"
    :class="{ 'layout-footer--gap': hideMobileFooterContent }"
  >
    <div
      class="layout-footer__contacts"
      :class="{ 'layout-footer__contacts--gap': hideMobileFooterContent }"
    >
      <LayoutFooterTitledButtons
        v-if="showContacts"
        :title="t('footer.needHelp')"
        class="layout-footer__contact"
      >
        <button
          type="button"
          class="layout-footer__link"
          @click="openContactModal"
        >
          <span class="layout-footer__font layout-footer__font--link">
            {{ t('footer.contactUs') }}
          </span>
        </button>
      </LayoutFooterTitledButtons>
    </div>

    <div
      class="layout-footer__wrapper"
      :class="{ 'hidden-mobile': hideMobileFooterContent }"
    >
      <div class="layout-footer__wrapper-container">
        <div class="layout-footer__body">
          <LayoutFooterNavigation
            class="layout-footer__navigation"
            :items="navigation"
          />

          <div class="layout-footer__awards">
            <div
              v-for="(award, index) in awards"
              :key="index"
              class="layout-footer__award"
            >
              <img
                class="layout-footer__award-image"
                :src="award.image"
                :alt="award.type"
                loading="lazy"
              />

              <div class="layout-footer__award-content">
                <div class="layout-footer__award-title">
                  <span
                    class="layout-footer__font layout-footer__font--award-title"
                  >
                    {{ tp(t(award.title)) }}
                  </span>
                </div>

                <div v-if="award.text" class="layout-footer__award-text">
                  <span
                    class="layout-footer__font layout-footer__font--award-text"
                  >
                    {{ t(award.text) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="layout-footer__bottom">
          <div class="layout-footer__content">
            <div class="layout-footer__icons">
              <CImage
                v-for="icon in icons"
                :key="icon.name"
                class="layout-footer__icon"
                :class="`layout-footer__icon--${icon.name}`"
                :src="`/icons/footer/${icon.name}.svg`"
                :title="icon.title"
                :alt="icon.title"
                loading="lazy"
              />
            </div>

            <div class="layout-footer__copyrights">
              <span
                class="layout-footer__font layout-footer__font--copyrights"
                v-html="tp(t('footer.copyrights', { year }))"
              />
            </div>
          </div>

          <div class="layout-footer__socials">
            <a
              v-for="social in socials"
              :key="social.title"
              class="layout-footer__social"
              target="_blank"
              :href="social.link"
              :title="social.title"
              rel="nofollow"
            >
              <CIcon
                class="layout-footer__icon layout-footer__icon--social"
                :name="social.icon"
              />
            </a>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<style scoped lang="scss">
.layout-footer {
  position: relative;

  &__font {
    display: block;

    &--link {
      font-size: em(18);
      font-weight: 500;
    }

    &--award-title {
      font-size: em(18);
      font-weight: 600;
      line-height: math.div(22, 18);

      @include media-breakpoint-down(sm) {
        font-size: em(13);
        line-height: math.div(15, 13);
      }
    }

    &--award-text {
      font-size: em(18);
      line-height: math.div(22, 18);

      @include media-breakpoint-down(sm) {
        font-size: em(13);
        line-height: math.div(15, 13);
      }
    }

    &--copyrights {
      font-size: em(14);
      font-weight: 400;
      line-height: math.div(18, 14);
    }
  }

  &__icon {
    flex-shrink: 0;
    object-fit: contain;

    //&--social {
    //  width: em(62);
    //  height: em(62);
    //}

    &--visa {
      width: em(66);
      height: em(21);

      @include media-breakpoint-down(sm) {
        width: em(85);
        height: em(28);
      }
    }

    &--mastercard {
      width: em(44);
      height: em(27);

      @include media-breakpoint-down(sm) {
        width: em(57);
        height: em(35);
      }
    }

    &--google {
      @include box(em(26));

      @include media-breakpoint-down(sm) {
        @include box(em(33));
      }
    }

    &--apple-black {
      width: em(25);
      height: em(32);

      @include media-breakpoint-down(sm) {
        width: em(32);
        height: em(41);
      }
    }

    &--pay {
      width: em(42);
      height: em(25);

      @include media-breakpoint-down(sm) {
        width: em(53);
        height: em(32);
      }
    }

    &--social {
      @include box(em(30));

      flex-shrink: 0;
      color: $color-black;
      transition: color $time-3;

      @include hover {
        color: $color-main-1;
      }
    }
  }

  &__icons {
    display: flex;
    gap: em(16);
    align-items: center;

    @include media-breakpoint-down(sm) {
      justify-content: space-between;
      width: 100%;
    }
  }

  &__contacts {
    position: absolute;
    bottom: 100%;
    inset-inline-start: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-block: em(24);
    padding-inline: em($gap);

    &:empty {
      display: none;
    }

    @include media-breakpoint-down(sm) {
      width: 100%;
      padding-block: em(32);
      padding-inline: em($gap-sm);
    }

    &--gap {
      @include media-breakpoint-down(sm) {
        bottom: em(132);
      }
    }
  }

  // &__contact {
  //   @include media-breakpoint-down(sm) {
  //     margin-inline-end: em(10);
  //   }
  // }

  &__link {
    color: $color-main-1;
    transition: color $time-3;

    @include hover {
      color: $color-second-1;
    }
  }

  &__button {
    &--social {
      &:not(:last-child) {
        margin-inline-end: em(16);
      }
    }
  }

  &__wrapper {
    padding-block-start: em(48);
    padding-inline: em(80);
    padding-block-end: em(76);
    background-color: $color-gray-5;

    @include media-breakpoint-down(sm) {
      padding-block-start: em(32);
      padding-inline: em($gap-sm);
      padding-block-end: em(42);
    }
  }

  &__body {
    display: flex;
    align-items: center;
    margin-bottom: em(42);

    @include media-breakpoint-down(sm) {
      flex-direction: column;
      align-items: flex-start;
      margin-bottom: em(35);
    }
  }

  &__navigation {
    flex-shrink: 0;

    @include media-breakpoint-up(sm) {
      margin-inline-end: em(125);
    }

    @include media-breakpoint-down(sm) {
      margin-bottom: em(41);
    }
  }

  &__awards {
    display: flex;
    flex-grow: 1;
    align-items: center;
    justify-content: space-between;
    max-width: em(960);

    @include media-breakpoint-down(sm) {
      flex-direction: column;
      gap: em($gap-sm);
      align-items: flex-start;
    }
  }

  &__award {
    display: flex;
    gap: em(14);
    align-items: center;

    @include media-breakpoint-up(sm) {
      white-space: pre-line;
    }

    @include media-breakpoint-down(sm) {
      gap: em(11);
    }
  }

  &__award-image {
    @include box(em(100));

    flex-shrink: 0;
    object-fit: contain;

    @include media-breakpoint-down(sm) {
      @include box(em(60));
    }
  }

  &__info {
    width: 100%;
  }

  &__bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;

    @include media-breakpoint-down(sm) {
      flex-direction: column-reverse;
    }
  }

  &__content {
    display: flex;
    gap: em(40);
    align-items: center;

    @include media-breakpoint-down(sm) {
      flex-direction: column;
      gap: em(30);
      align-items: flex-start;
      padding-top: em(32);
      border-top: 1px solid $color-gray-3;
    }
  }

  &__socials {
    display: flex;
    gap: em(27);
    align-items: center;

    @include media-breakpoint-down(sm) {
      justify-content: space-between;
      width: 100%;
      margin-bottom: em(39);
    }
  }

  &__copyrights {
    max-width: em(960);
    color: rgba($color-text, 0.7);

    :deep(br) {
      @include media-breakpoint-down(sm) {
        display: none;
      }
    }
  }

  &--gap {
    @include media-breakpoint-down(sm) {
      padding-top: em(100);
    }
  }
}
</style>
